import { ApiDashboardModelSpiderChartDataRetrieveLimitEnum, DashboardSpiderModel, DashboardSpiderUseCase } from '@api';
import { Dropdown, DropdownItemType } from '@components/ui';
import { useApi } from '@data-access';
import { chartColors, MapperHelper } from '@helpers';
import { indicatorsLabels } from '@lib';
import { notionStore } from '@stores/notion.store';
import {
  Chart as ChartJS,
  ChartOptions,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import clsx from 'clsx';
import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Radar } from 'react-chartjs-2';
import { DashboardApiFilterParameters } from 'src/pages/dashboard';

import { ChartCard } from '../ChartCard/ChartCard';

import styles from './RiskRadar.module.scss';

ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

function label(context: any) {
  return `${context.dataset.label}: ${MapperHelper.roundNumber(context.parsed.r * 100, 2)}%`;
}

const options: ChartOptions<'radar'> = {
  layout: { autoPadding: true },
  devicePixelRatio: 2.5,
  plugins: {
    datalabels: { display: false },
    title: {
      display: false,
      text: 'Risk indicators',
    },
    legend: {
      position: 'bottom' as any,
      labels: {
        pointStyle: 'rectRounded',
        usePointStyle: true,
        padding: 16,
      },
    },
    tooltip: {
      padding: 6,
      backgroundColor: '#3d5a80d4',
      callbacks: {
        label,
      },
    },
  },
  responsive: true,
  maintainAspectRatio: false,
  datasets: {
    radar: { borderDash: [8, 4], borderWidth: 2 },
  },
  scales: {
    r: {
      ticks: {
        color: '#3d5a80',
        callback(value: any) {
          return `${MapperHelper.roundNumber(value * 100, 1)}%`;
        },
      },
      suggestedMin: 0,
      suggestedMax: 1,
      pointLabels: { color: '#3d5a80', font: { size: 11, weight: 500 } },
    },
  },
};

const colors = indicatorsLabels.map(indicator => ({
  backgroundColor: chartColors[indicator.toLowerCase()].backgroundColor.replace('b4', '5a'),
  pointBackgroundColor: chartColors[indicator.toLowerCase()].pointBackgroundColor,
  borderColor: chartColors[indicator.toLowerCase()].borderColor,
}));
const dropdownItems: DropdownItemType[] = [
  { label: 'Models', value: 'models' },
  { label: 'Use cases', value: 'useCases' },
];

interface RiskRadarProbProps {
  appliedFilters: DashboardApiFilterParameters;
  fixedWidth?: number;
}

export const RiskRadarProb: React.FC<RiskRadarProbProps> = observer(({ appliedFilters, fixedWidth }) => {
  const { dashboardApi } = useApi();
  const [models, setModels] = useState<DashboardSpiderModel[]>();
  const [useCases, setUseCases] = useState<DashboardSpiderUseCase[]>();
  const { values } = notionStore;

  const [selectedType, setSelectedType] = useState<DropdownItemType>({ value: 'models', label: 'Models' });

  // set default
  const [modelQuantity, setModelQuantity] = useState<DropdownItemType>();
  const [useCaseQuantity, setUseCaseQuantity] = useState<DropdownItemType>();
  const [maxCountModels, setMaxCountModels] = useState<number>(0);
  const [maxCountUseCases, setMaxCountUseCases] = useState<number>(0);

  const getModels = useCallback(async () => {
    const { results: fetchedModels, maxCount } = await dashboardApi.apiDashboardModelSpiderChartDataRetrieve({
      ...appliedFilters,
      limit: 6,
    });
    setModels(fetchedModels);
    setMaxCountModels(maxCount);
  }, [dashboardApi, appliedFilters]);

  useEffect(() => {
    getModels();
  }, [getModels]);

  const getUsecases = useCallback(async () => {
    const { results: fethcedUsecases, maxCount } = await dashboardApi.apiDashboardUseCaseSpiderChartDataRetrieve({
      ...appliedFilters,
      limit: 6,
    });
    setUseCases(fethcedUsecases);
    setMaxCountUseCases(maxCount);
  }, [dashboardApi, appliedFilters]);

  useEffect(() => {
    getUsecases();
  }, [getUsecases]);

  const getModelsByLimit = useCallback(
    async (limit: ApiDashboardModelSpiderChartDataRetrieveLimitEnum) => {
      const { results: fetchedModels } = await dashboardApi.apiDashboardModelSpiderChartDataRetrieve({
        ...appliedFilters,
        limit,
      });
      setModels(fetchedModels);
    },
    [dashboardApi, appliedFilters],
  );

  useEffect(() => {
    if (!modelQuantity?.value) return;
    getModelsByLimit(modelQuantity.value as ApiDashboardModelSpiderChartDataRetrieveLimitEnum);
  }, [modelQuantity?.value, getModelsByLimit]);

  const getUseCasesByLimit = useCallback(
    async (limit: ApiDashboardModelSpiderChartDataRetrieveLimitEnum) => {
      const { results: fetchedUsecases } = await dashboardApi.apiDashboardUseCaseSpiderChartDataRetrieve({
        ...appliedFilters,
        limit,
      });
      setUseCases(fetchedUsecases);
    },
    [dashboardApi, appliedFilters],
  );

  useEffect(() => {
    if (!useCaseQuantity?.value) return;
    getUseCasesByLimit(useCaseQuantity.value as ApiDashboardModelSpiderChartDataRetrieveLimitEnum);
  }, [useCaseQuantity?.value, getUseCasesByLimit]);

  const modelsData = useMemo(() => {
    if (!models) return { labels: [], datasets: [] };
    const datasets = [];
    for (let i = 0; i < models.length; i += 1) {
      const model = models[i];
      datasets.push({
        label: model.aiModel,
        data: [model.performanceValue, model.robustnessValue, model.fairnessValue, model.explainabilityValue],
        ...colors[i],
      });
    }
    return { labels: indicatorsLabels, datasets };
  }, [models]);

  const useCasesData = useMemo(() => {
    if (!useCases) return { labels: [], datasets: [] };
    const datasets = [];
    for (let i = 0; i < useCases.length; i += 1) {
      const useCase = useCases[i];
      datasets.push({
        label: useCase.useCase,
        data: [useCase.performanceValue, useCase.robustnessValue, useCase.fairnessValue, useCase.explainabilityValue],
        ...colors[i],
      });
    }
    return { labels: indicatorsLabels, datasets };
  }, [useCases]);

  // generate numbers for quantity array
  const quantityArray = useMemo(() => {
    const numbers = [];
    let limit = selectedType.value === 'models' ? maxCountModels : maxCountUseCases;
    if (limit > 6) limit = 6;
    for (let i = 0; i < limit; i += 1) {
      numbers.push({ label: (i + 1).toString(), value: i + 1 });
    }
    setModelQuantity(
      maxCountModels > 6 ? { label: '6', value: 6 } : { label: maxCountModels.toString(), value: maxCountModels },
    );
    setUseCaseQuantity(
      maxCountUseCases > 6 ? { label: '6', value: 6 } : { label: maxCountUseCases.toString(), value: maxCountUseCases },
    );
    return numbers;
  }, [selectedType, maxCountModels, maxCountUseCases]);

  return (
    <ChartCard
      className={clsx(fixedWidth ? styles.reportCard : styles.dashboardCard)}
      title="Risk Radar"
      subtitle="Risk indicator radar of the riskiest models / use-cases in the filtered portfolio"
      infoPosition="right"
      info={values?.riskRadar || ' '}
    >
      <Dropdown
        className={styles.dropdown}
        onSelect={item => {
          setSelectedType(item);
        }}
        items={dropdownItems}
        selected={selectedType}
      />
      <Dropdown
        className={styles.dropdownQuantity}
        onSelect={item => {
          if (selectedType.value === 'models') {
            setModelQuantity(item);
          } else {
            setUseCaseQuantity(item);
          }
        }}
        items={quantityArray}
        selected={
          selectedType.value === 'models'
            ? modelQuantity || { label: '3', value: 3 }
            : useCaseQuantity || { label: '3', value: 3 }
        }
      />
      {fixedWidth ? (
        <Radar
          data={selectedType.value === 'models' ? modelsData : useCasesData}
          options={options}
          height={500}
          width={fixedWidth}
        />
      ) : (
        <div className={styles.chart}>
          <Radar
            data={selectedType.value === 'models' ? modelsData : useCasesData}
            options={options}
          />
        </div>
      )}
    </ChartCard>
  );
});
